// chinThreadsData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}


interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const chinThreads: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Chin Threading in Dubai | Non-Surgical Thread Lift for Chin & Jawline",
            "description": "Refine your chin and jawline with Chin Threading in Dubai. Our thread lift treatment enhances contour and tightens skin for a youthful, defined look—no surgery required.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/chin-threads/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Chin Threading in Dubai | Non-Surgical Thread Lift for Chin & Jawline",
                "description": "Chin thread lifts are a popular non-invasive treatment to improve chin contour, reduce sagging, and create a more sculpted jawline—all without surgery or downtime.",
                "url": "https://elitebodyhome.com/services/chin-threads/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-16T13:43:31+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Chin-Threads-in-dubai-2.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Chin-Threads-in-dubai-2.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Chin Threading",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Chin Threading in Dubai | Non-Surgical Thread Lift for Chin & Jawline",
                "description": "Define your chin and jawline with our advanced chin threading treatment in Dubai. Safe, non-surgical, and effective tightening results with minimal recovery time.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Chin-Threads-in-dubai-2.jpg"
            },
        },
        "breadcrumbs": ["Home", "Services", "Chin Threads"],
        "hero": {
            "title": "Chin Threads",
            "subtitle": "Contour Your Chin, Elevate Your Look",
            "description": "Chin threads (or thread lifts) are popular treatments for non-surgical cosmetic procedures to improve the shape of the chin and define the jawline. This non-invasive procedure provides a renewed, youthful look with tight and lifted skin in the specific area. If you want to redefine your chin without invasive surgery for a more sculpted look, chin threads could be just what you need."
        },
        "howItWorks": {
            "title": "Chin Threading Procedure",
            "description": "Chin thread liftTreatment involves the placement of absorbable threads to lift the chin or to lift the skin of the jawline. During the procedure, very thin, medical-grade threads composed of either polydioxanone (PDO) or poly-L-lactic acid (PLA) are injected into the skin using a tiny needle. These threads serve as scaffolding under the skin, lifting sagging tissue and stimulating collagen production over time."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "The process begins with an in-depth consultation where an aesthetic practitioner examines your facial structure and determines your beauty goals.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "Preparation",
                    "desc": "The area under the chin is first cleaned and local anesthesia is applied to ensure comfort during the procedure.",
                    "icon": "fa-solid fa-prescription-bottle-medical"
                },
                {
                    "title": "Thread Insertion",
                    "desc": "Using a very thin needle or cannula, the threads are inserted into the skin at very specific sites until the desired lifting effect is achieved.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Adjustment",
                    "desc": "Once the threads are secured into place, the practitioner then carefully adjusts the threads to optimise the lift, producing a symmetrical, natural-looking outcome.",
                    "icon": "fa-solid fa-sliders"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "",
            "para": "Chin thread lifts are a non surgical cosmetic procedure that work by inserting stitches into the chin area to tighten and lift the skin. They offer quick results with very little recovery, which can be a appealing option for individuals with fast-paced lives. Here are some key advantages:",
            "items": [
                {
                    "title": "Non-surgical",
                    "desc": "There are no incisions or general anesthesia needed, lowering the risks associated with surgery.",
                    "icon": "fa-solid fa-hand-sparkles"
                },
                {
                    "title": "Immediate lifting effect",
                    "desc": "Immediately, you'll notice a sharper appearance and definition in your chin.",
                    "icon": "fa-solid fa-arrow-up"
                },
                {
                    "title": "Collagen stimulation",
                    "desc": "The threads trigger natural collagen production over time, promoting the tightening and firmness of your skin.",
                    "icon": "fa-solid fa-seedling"
                },
                {
                    "title": "Minimal downtime",
                    "desc": "Unlike with surgery, recovery time is short, and most patients can return to regular activities within a day or two.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "Natural-looking results",
                    "desc": "It gives subtle lift without a caked-on or fake look, and it enhances your natural features.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Reversible and adjustable",
                    "desc": "The threads are eventually absorbed over time, allowing for corrections, when necessary.",
                    "icon": "fa-solid fa-arrows-rotate"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "Chin threads are appropriate for people who have not yet lost the elasticity in the skin of their chin and jawline but recognize that it is starting to sag or feel loose and are not ready to commit to a more invasive surgical procedure such as a facelift.",
            "items": [
                {
                    "title": "Mild to moderate skin laxity",
                    "desc": "Suitable for those with moderate sagging around the chin looking for a slight lift.",
                    "icon": "fa-solid fa-check-circle"
                },
                {
                    "title": "Good overall health",
                    "desc": "It is contraindicated in patients with active infections, morbid medical conditions, and patients with a history of keloid scarring.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "Realistic expectations",
                    "desc": "It's important to understand that although the results are noticeable, they're more subtle than surgery.",
                    "icon": "fa-solid fa-scale-balanced"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "",
            "para": "Before checking into the chin thread lift procedure, there are some things you need to do to ensure positive results as well as to avoid the worst complications.",
            "items": [
                {
                    "title": "Avoid blood thinners",
                    "desc": "Stop taking medications such as aspirin, ibuprofen, or any supplements that can thin the blood for at least a week before the procedure.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Stop smoking",
                    "desc": "If you smoke, it's best to stop at least two weeks before treatment, as smoking can delay healing.",
                    "icon": "fa-solid fa-smoking-ban"
                },
                {
                    "title": "Avoid alcohol",
                    "desc": "Avoid alcohol for 24-48 hours prior to your procedure as this can increase bruising.",
                    "icon": "fa-solid fa-wine-glass-empty"
                },
                {
                    "title": "Discuss any medications",
                    "desc": "Inform your practitioner about any medications or supplements you are currently taking to avoid potential interactions.",
                    "icon": "fa-solid fa-file-medical"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "Not only does it provide instant results, chin threads for facial rejuvenation can also improve over time, which makes it one of the most effective treatments around. The result of a chin thread lift are visible straight away, giving your chin and jawline a more defined and youthful contour. Then, over the following months, as collagen production ramps up, the skin around your chin tightens and smooths. These results can last anywhere from six to 18 months based on the type of threads used and your skin's natural aging process.",
            "items": [
                {
                    "title": "Before & After Results",
                    "desc": "",
                    "icon": "fa-solid fa-images"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How long do chin threads last?",
                    "answer": "The immediate lifting effect is visible right away, but the final results improve as collagen builds. The threads themselves dissolve over 6 to 12 months, while the collagen effects can last up to 18 months or more."
                },
                {
                    "question": "Is the procedure painful?",
                    "answer": "Most patients experience minimal discomfort thanks to the local anesthetic applied before the treatment. You may feel some slight pressure during the thread insertion, but it's typically not painful."
                },
                {
                    "question": "What are the side effects?",
                    "answer": "Some temporary side effects include mild swelling, bruising, and tenderness around the treated area. These usually resolve within a few days."
                },
                {
                    "question": "Can chin threads be combined with other treatments?",
                    "answer": "Yes, chin threads can be combined with other non-invasive treatments such as dermal fillers or Botox to further enhance facial contouring and rejuvenation."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Chin-Threads-in-dubai-2.jpg",
            beforeAfter: [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Chin-Threads-before-after-in-dubai-1.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Chin-Threads-before-after-in-dubai-2.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Chin-Threads-before-after-in-dubai-3.jpg"
            ]
        },
        "product": {
            "id": 211,
            "title": "Chin Threading",
            "price": 599,
            "description": "Chin Threading at Elite Body Home offers a non-surgical thread lift solution that lifts, defines, and rejuvenates the lower face using dissolvable threads for natural results."
        }
    },
    "ar": {
        "url": "",
        "meta": {
            "title": "شد الذقن بالخيوط في دبي | رفع الذقن وخط الفك بدون جراحة",
            "description": "حسّن شكل ذقنك وخط فكك مع علاج شد الذقن بالخيوط في دبي. هذا الإجراء غير الجراحي يعمل على رفع وتحديد ملامح الوجه للحصول على مظهر أكثر شبابًا ووضوحًا.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/chin-threads/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "شد الذقن بالخيوط في دبي | رفع الذقن وخط الفك بدون جراحة",
                "description": "شد الذقن بالخيوط هو علاج غير جراحي شعبي لتحسين شكل الذقن، تقليل الترهل، والحصول على خط فك أكثر تحديدًا - كل ذلك بدون جراحة أو فترة نقاهة.",
                "url": "https://elitebodyhome.com/services/chin-threads/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-16T13:43:31+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Chin-Threads-in-dubai-2.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Chin-Threads-in-dubai-2.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "شد الذقن بالخيوط",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "شد الذقن بالخيوط في دبي | رفع الذقن وخط الفك بدون جراحة",
                "description": "حدد ذقنك وخط فكك مع علاج شد الذقن بالخيوط المتقدم في دبي. نتائج آمنة وفعالة بدون جراحة مع وقت تعافي قصير.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Chin-Threads-in-dubai-2.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "شد الذقن بالخيوط"],
        "hero": {
            "title": "شد الذقن بالخيوط",
            "subtitle": "حدد ذقنك، وعزز مظهرك",
            "description": "يعد شد الذقن بالخيوط أو خيوط الذقن من الإجراءات التجميلية غير الجراحية الشائعة لتحسين شكل الذقن وتحديد خط الفك. يوفر هذا الإجراء غير الجراحي مظهرًا متجددًا وشابًا مع بشرة مشدودة وتأثير رفع للجلد في المنطقة المعالجة. إذا كنت ترغب في إعادة تحديد ذقنك دون جراحة للحصول على مظهر أكثر تحديدًا، فقد يكون شد الذقن بالخيوط هو الحل الأنسب لك."
        },
        "howItWorks": {
            "title": "إجراء شد الذقن بالخيوط",
            "description": "يتضمن علاج شد الذقن بالخيوط وضع خيوط قابلة للامتصاص لرفع الذقن أو شد الجلد المحيط بخط الفك. تُحقن خيوط طبية دقيقة جدًا داخل الجلد باستخدام إبرة رفيعة، تُصنع هذه الخيوط من مادة البوليديوكسانون (PDO) أو حمض البولي لاكتيك (PLA)، وتعمل كدعامة تحت الجلد، لرفع الأنسجة المترهلة وتحفيز إنتاج الكولاجين تدريجيًا مع مرور الوقت."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "الاستشارة",
                    "desc": "يبدأ الإجراء بجلسة استشارية شاملة يقوم فيها طبيب التجميل بفحص بنية وجهك وتحديد أهدافك الجمالية.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "الاستعداد للعلاج",
                    "desc": "تُنظف المنطقة أسفل الذقن أولًا، ثم يُستخدم مخدر موضعي لضمان راحتك أثناء الإجراء.",
                    "icon": "fa-solid fa-prescription-bottle-medical"
                },
                {
                    "title": "إدخال الخيوط",
                    "desc": "تُدخل الخيوط داخل الجلد في نقاط محددة بدقة باستخدام إبرة أو قنية رفيعة جدًا حتى يتحقق تأثير الشد المطلوب.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "تشكيل الخيوط",
                    "desc": "يقوم الطبيب بتعديل الخيوط بعناية بعد تثبيتها في أماكنها لتحقيق أفضل تأثير لرفع الجلد، مما يمنحك مظهرًا متناسقًا وطبيعيًا.",
                    "icon": "fa-solid fa-sliders"
                }
            ]
        },
        "benefits": {
            "heading": "الفوائد",
            "subheading": "",
            "para": "يُعد شد الذقن بالخيوط إجراءً تجميليًا غير جراحي يعمل من خلال إدخال خيوط دقيقة في منطقة الذقن لشد الجلد ورفعه. يوفر نتائج سريعة مع وقت تعافي قصير، مما يجعله خيارًا جذابًا للأشخاص ذوي نمط الحياة السريع. فيما يلي بعض المزايا الرئيسية:",
            "items": [
                {
                    "title": "إجراء غير جراحي",
                    "desc": "لا يتطلب أي شقوق جراحية أو تخدير عام، مما يقلل من المخاطر المرتبطة بالعمليات الجراحية.",
                    "icon": "fa-solid fa-hand-sparkles"
                },
                {
                    "title": "تأثير رفع فوري للذقن",
                    "desc": "تُلاحظ مظهرًا أكثر تحديدًا وحدّة في منطقة الذقن مباشرًة بعد الإجراء.",
                    "icon": "fa-solid fa-arrow-up"
                },
                {
                    "title": "تحفيز إنتاج الكولاجين",
                    "desc": "تُحفز الخيوط إنتاج الكولاجين الطبيعي تدريجيًا، مما يساهم في شد البشرة وتحسين مرونتها بمرور الوقت.",
                    "icon": "fa-solid fa-seedling"
                },
                {
                    "title": "فترة تعافي قصيرة",
                    "desc": "يتطلب وقت تعافي قصيرًا بعد الإجراء على عكس الإجراءات الجراحية، وغالبًا ما يمكن للمرضى العودة إلى أنشطتهم اليومية خلال يوم أو يومين.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "نتائج طبيعية",
                    "desc": "يحقق شد الذقن بالخيوط مظهرًا مشدودًا بشكل خفيف دون أن يبدو مصطنعًا، ويُبرز ملامح الوجه الطبيعية.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "إجراء قابل للتعديل والتراجع",
                    "desc": "تذوب الخيوط تدريجيًا بمرور الوقت، مما يتيح إجراء التعديلات أو التصحيحات إذا لزم الأمر.",
                    "icon": "fa-solid fa-arrows-rotate"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "يناسب شد الذقن بالخيوط الأشخاص الذين لم يفقدوا مرونة الجلد بالكامل في منطقة الذقن وخط الفك، لكنهم بدأوا يلاحظون بعض الترهل أو الارتخاء في الجلد، ولا يرغبون في الخضوع لإجراء جراحي مثل شد الوجه.",
            "items": [
                {
                    "title": "ترهل خفيف إلى متوسط في الجلد",
                    "desc": "يلائم الأشخاص الذين يعانون من ترهل بسيط في منطقة الذقن ويبحثون عن رفع للجلد خفيف وطبيعي.",
                    "icon": "fa-solid fa-check-circle"
                },
                {
                    "title": "صحة عامة جيدة",
                    "desc": "لا يُوصى به للأشخاص الذين يعانون من التهابات نشطة، أو حالات صحية مزمنة خطيرة، أو لديهم تاريخ صحي مع ظهور ندبات الجدرة.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "توقعات واقعية",
                    "desc": "يجب إدراك أن النتائج رغم أنها واضحة، لكنها تكون طبيعية و أكثر نعومة من نتائج العمليات الجراحية.",
                    "icon": "fa-solid fa-scale-balanced"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "",
            "para": "يجب اتباع بعض التعليمات الهامة قبل الخضوع لإجراء شد الذقن بالخيوط، لضمان نتائج إيجابية وتقليل أي مخاطر أو مضاعفات.",
            "items": [
                {
                    "title": "تجنب مُميعات الدم",
                    "desc": "توقف عن تناول الأدوية مثل الأسبرين، أو الإيبوبروفين، أو أي مكملات غذائية مُميعة للدم، وذلك لمدة أسبوع على الأقل قبل الجلسة.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "الامتناع عن التدخين",
                    "desc": "إذا كنت مدخنًا، يُنصح بالتوقف عن التدخين قبل أسبوعين على الأقل من الإجراء، لأنه يبطئ عملية الشفاء.",
                    "icon": "fa-solid fa-smoking-ban"
                },
                {
                    "title": "الامتناع عن الكحول",
                    "desc": "تجنب شرب الكحول لمدة 24 إلى 48 ساعة قبل العلاج، لأنه قد يرفع من احتمالية ظهور الكدمات.",
                    "icon": "fa-solid fa-wine-glass-empty"
                },
                {
                    "title": "إبلاغ الطبيب عن أي الأدوية",
                    "desc": "أخبر الطبيب بجميع الأدوية أو المكملات الغذائية التي تتناولها بانتظام، لتفادي أي تداخلات.",
                    "icon": "fa-solid fa-file-medical"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "لا يمنحك شد الذقن بالخيوط نتائج فورية فقط، بل تستمر النتائج في التحسن مع مرور الوقت، مما يجعله أحد أكثر الإجراءات التجميلية فعالية. تظهر نتائج شد الذقن بالخيوط مباشرةً بعد الإجراء، مما يمنح الذقن وخط الفك تحديدًا أوضح ومظهرًا أكثر شبابًا. ثم تبدأ البشرة المحيطة بالذقن في التماسك وتصبح أكثر نعومة نتيجة لزيادة إنتاج الكولاجين خلال الأشهر التالية للجلسة. يمكن أن تستمر هذه النتائج من ستة إلى ثمانية عشر شهراً، بناءً على نوع الخيوط المستخدمة وعملية الشيخوخة الطبيعية للبشرة.",
            "items": [
                {
                    "title": "النتائج قبل وبعد العلاج",
                    "desc": "",
                    "icon": "fa-solid fa-images"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "كم تدوم نتائج شد الذقن بالخيوط؟",
                    "answer": "يظهر تأثير الرفع الفوري مباشرة بعد الإجراء، لكن النتائج النهائية تتحسن مع زيادة إنتاج الكولاجين. تذوب الخيوط نفسها خلال 6 إلى 12 شهرًا، بينما يمكن أن تستمر تأثيرات الكولاجين حتى 18 شهرًا أو أكثر."
                },
                {
                    "question": "هل الإجراء مؤلم؟",
                    "answer": "يشعر معظم المرضى بألم بسيط بفضل التخدير الموضعي المستخدم قبل العلاج. قد تشعر ببعض الضغط أثناء إدخال الخيوط، لكنه عادة لا يكون مؤلمًا."
                },
                {
                    "question": "ما هي الآثار الجانبية؟",
                    "answer": "تشمل الآثار الجانبية المؤقتة تورمًا خفيفًا، كدمات، وألمًا بسيطًا في المنطقة المعالجة. تختفي هذه الآثار عادةً خلال بضعة أيام."
                },
                {
                    "question": "هل يمكن الجمع بين شد الذقن بالخيوط والعلاجات الأخرى؟",
                    "answer": "نعم، يمكن الجمع بين شد الذقن بالخيوط والعلاجات غير الجراحية الأخرى مثل الفيلر أو البوتوكس لتعزيز نتائج تجديد وتحديد ملامح الوجه."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    }
};
